// Raw doc-blob endpoints (opaque octet-stream; the REST persistence adapter
// serializes the CADDocument as JSON in/out). Extracted from api/client.ts
// (verbatim); consumed by api.ts (loadDoc / saveDoc / snapshotDoc).
import { API_BASE } from './constants';
import { parseError } from './errors';
import { ensureCsrf, clearCsrf } from './csrf';

// ── Raw doc blob (the /doc endpoint is opaque octet-stream; the REST persistence
// adapter serializes the CADDocument as JSON into/out of it). ───────────────────
export async function loadDocBlob(modelId: string): Promise<string | null> {
  const res = await fetch(`${API_BASE}/models/${modelId}/doc`, { credentials: 'include' });
  if (res.status === 404) return null;
  if (!res.ok) throw await parseError(res);
  return res.text();
}

export async function saveDocBlob(modelId: string, blob: string, retried = false): Promise<void> {
  const csrf = await ensureCsrf();
  const res = await fetch(`${API_BASE}/models/${modelId}/doc`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/octet-stream', 'X-CSRF-Token': csrf },
    body: blob,
  });
  if (!res.ok) {
    const err = await parseError(res);
    // The /doc save bypasses request(), so it must repeat request()'s one-shot
    // CSRF refresh — otherwise a single stale-token 403 (e.g. another tab just
    // rotated the cookie) surfaces straight to the user as "Save failed".
    if (err.status === 403 && !retried && !err.code && /csrf/i.test(err.message)) {
      clearCsrf();
      return saveDocBlob(modelId, blob, true);
    }
    throw err;
  }
}

// D3: a snapshot's serialized doc (octet-stream JSON string) for client-side
// materialization (restore/switch reset the live room; entity-history diffs).
export async function snapshotDocBlob(modelId: string, snapshotId: string): Promise<string> {
  const res = await fetch(`${API_BASE}/models/${modelId}/snapshots/${snapshotId}/doc`, {
    credentials: 'include',
  });
  if (!res.ok) throw await parseError(res);
  return res.text();
}
